import React from 'react';
import { GlassModal, GlassModalHeader, GlassModalBody, GlassModalFooter } from '../../components/GlassModal';
import GlassDataTable from '../../components/GlassDataTable';
import './companies-view.css';
import GlassFab from '../../components/glass/GlassFab';
import { Redirect } from "react-router-dom";
import SpinnerView from '../../components/spinner/spinner-view';

/* Local glass UI primitives (button + labeled field). */
const BTN_VARIANTS = {
  primary: { background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))', color: '#fff', border: 'none' },
  secondary: { background: 'rgba(15,31,46,0.06)', color: 'var(--text-secondary)', border: '1px solid rgba(15,31,46,0.10)' },
  default: { background: 'rgba(13,161,178,0.12)', color: 'var(--primary-dark)', border: '1px solid rgba(13,161,178,0.25)' },
  danger: { background: 'linear-gradient(135deg, #DA4A4F, #b83a3e)', color: '#fff', border: 'none' },
};
const Btn = ({ variant = 'primary', size, style, disabled, children, ...rest }) => (
  <button type="button" disabled={disabled} {...rest} style={{ borderRadius: 10, padding: size === 'sm' ? '6px 12px' : '9px 18px', fontWeight: 700, fontFamily: 'var(--font-display)', fontSize: size === 'sm' ? 12 : 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, marginLeft: 6, ...BTN_VARIANTS[variant], ...style }}>{children}</button>
);
const Field = ({ label, type, rows, hint, className, style, ...rest }) => {
  const base = { width: '100%', padding: '11px 14px', border: '1px solid rgba(15,31,46,0.12)', borderRadius: 10, fontSize: 14, outline: 'none', background: 'rgba(255,255,255,0.7)', fontFamily: 'inherit', boxSizing: 'border-box' };
  return (
    <div className={className} style={{ marginBottom: 14, ...style }}>
      {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>{label}</label>}
      {type === 'textarea'
        ? <textarea rows={rows || 3} {...rest} style={{ ...base, resize: 'vertical' }} />
        : <input type={type || 'text'} {...rest} style={base} />}
      {hint && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
};

class CompanyView extends React.Component {

  state = {
    modal: false,
    wizardStep: 1,
    // Step 1
    companyName: '',
    // Step 2
    businessDescription: '',
    suggesting: false,
    suggestError: null,
    // Step 3
    suggestions: null,         // { entityTemplates, invoiceTemplate, appointmentTypes }
    enabledEntityTemplates: [], // array of booleans matching suggestions.entityTemplates
    enabledAppointmentTypes: [], // array of booleans matching suggestions.appointmentTypes
    refinement: '',
    refining: false,
    // Creating
    creating: false,
    // Table sort
    sortField: 'name',
    sortDir: 'asc',
    // Settings modal
    settingsCompany: null,
    deleteConfirm: '',
    deleting: false,
  }

  handleDeleteCompany = async () => {
    const { settingsCompany, deleteConfirm } = this.state;
    if (deleteConfirm !== settingsCompany.companyName) return;
    this.setState({ deleting: true });
    await this.props.dispatchDeleteCompany(settingsCompany._id);
    this.setState({ settingsCompany: null, deleteConfirm: '', deleting: false });
  }

  handleTableSort = (field) => {
    this.setState(prev => ({
      sortField: field,
      sortDir: prev.sortField === field && prev.sortDir === 'asc' ? 'desc' : 'asc',
    }));
  }

  toggle = () => {
    this.setState({
      modal: !this.state.modal,
      wizardStep: 1,
      companyName: '',
      businessDescription: '',
      suggesting: false,
      suggestError: null,
      suggestions: null,
      enabledEntityTemplates: [],
      enabledAppointmentTypes: [],
      refinement: '',
      refining: false,
      creating: false,
    });
  }

  // Step 1 → Step 2
  handleNextStep1 = () => {
    if (!this.state.companyName.trim()) return;
    this.setState({ wizardStep: 2, suggestError: null });
  }

  // Step 2: Generate templates from AI
  handleGenerate = async () => {
    const { businessDescription } = this.state;
    if (!businessDescription.trim()) return;

    this.setState({ suggesting: true, suggestError: null });
    try {
      const result = await this.props.dispatchSuggestTemplates(businessDescription.trim());
      if (result && result.entityTemplates) {
        this.setState({
          suggestions: result,
          enabledEntityTemplates: result.entityTemplates.map(() => true),
          enabledAppointmentTypes: result.appointmentTypes.map(() => true),
          wizardStep: 3,
          suggesting: false,
        });
      } else {
        this.setState({ suggestError: 'Could not generate templates. Please try again.', suggesting: false });
      }
    } catch (e) {
      this.setState({ suggestError: 'Error generating templates. Please try again.', suggesting: false });
    }
  }

  // Step 2: Skip AI setup, create with no templates
  handleSkip = () => {
    this.handleCreate(true);
  }

  // Step 3: Add refinement
  handleRefine = async () => {
    const { businessDescription, suggestions, refinement } = this.state;
    if (!refinement.trim()) return;

    this.setState({ refining: true });
    try {
      const result = await this.props.dispatchSuggestTemplates(
        businessDescription.trim(),
        suggestions,
        refinement.trim()
      );
      if (result && result.entityTemplates) {
        this.setState({
          suggestions: result,
          enabledEntityTemplates: result.entityTemplates.map(() => true),
          enabledAppointmentTypes: result.appointmentTypes.map(() => true),
          refinement: '',
          refining: false,
        });
      } else {
        this.setState({ refining: false });
      }
    } catch (e) {
      this.setState({ refining: false });
    }
  }

  // Step 3: Create company + selected templates
  handleCreate = async (skipTemplates = false) => {
    const { companyName, suggestions, enabledEntityTemplates, enabledAppointmentTypes } = this.state;

    const company = { companyName: companyName.trim() };
    let templates = null;

    if (!skipTemplates && suggestions) {
      templates = {
        entityTemplates: suggestions.entityTemplates.filter((_, i) => enabledEntityTemplates[i]),
        invoiceTemplate: suggestions.invoiceTemplate,
        appointmentTypes: suggestions.appointmentTypes.filter((_, i) => enabledAppointmentTypes[i]),
      };
    }

    this.setState({ creating: true });
    try {
      await this.props.dispatchCreateCompanyWithSetup(company, templates);
      this.setState({ modal: false, creating: false });
    } catch (e) {
      this.setState({ creating: false });
    }
  }

  toggleEntityTemplate = (index) => {
    const enabled = [...this.state.enabledEntityTemplates];
    enabled[index] = !enabled[index];
    this.setState({ enabledEntityTemplates: enabled });
  }

  toggleAppointmentType = (index) => {
    const enabled = [...this.state.enabledAppointmentTypes];
    enabled[index] = !enabled[index];
    this.setState({ enabledAppointmentTypes: enabled });
  }

  renderStep1() {
    const { companyName } = this.state;
    return (
      <>
        <GlassModalHeader toggle={this.toggle}>New Company</GlassModalHeader>
        <GlassModalBody>
          <Field
            label="Company Name"
            value={companyName}
            onChange={e => this.setState({ companyName: e.target.value })}
            onKeyPress={e => e.key === 'Enter' && this.handleNextStep1()}
          />
        </GlassModalBody>
        <GlassModalFooter>
          <Btn variant="secondary" onClick={this.toggle}>Cancel</Btn>
          <Btn variant="primary" onClick={this.handleNextStep1} disabled={!companyName.trim()}>
            Next
          </Btn>
        </GlassModalFooter>
      </>
    );
  }

  renderStep2() {
    const { businessDescription, suggesting, suggestError, companyName } = this.state;
    return (
      <>
        <GlassModalHeader toggle={this.toggle}>
          Set Up "{companyName}"
        </GlassModalHeader>
        <GlassModalBody>
          <p className="text-muted mb-3">
            Describe your business and we'll suggest the right templates for you.
          </p>
          <Field
            type="textarea"
            label="What type of business is this?"
            rows="3"
            value={businessDescription}
            onChange={e => this.setState({ businessDescription: e.target.value })}
            hint="e.g. veterinary practice that treats dogs and cats"
          />
          {suggestError && (
            <p className="text-danger mt-2">{suggestError}</p>
          )}
        </GlassModalBody>
        <GlassModalFooter>
          <Btn variant="secondary" onClick={() => this.setState({ wizardStep: 1 })}>Back</Btn>
          <Btn variant="default" onClick={this.handleSkip} disabled={suggesting}>
            Skip
          </Btn>
          <Btn
            variant="primary"
            onClick={this.handleGenerate}
            disabled={!businessDescription.trim() || suggesting}
          >
            {suggesting ? 'Generating...' : 'Generate'}
          </Btn>
        </GlassModalFooter>
      </>
    );
  }

  renderStep3() {
    const {
      suggestions, enabledEntityTemplates, enabledAppointmentTypes,
      refinement, refining, creating, companyName
    } = this.state;

    if (!suggestions) return null;
    const { entityTemplates, invoiceTemplate, appointmentTypes } = suggestions;
    const hasEnabled = enabledEntityTemplates.some(Boolean) || enabledAppointmentTypes.some(Boolean);

    return (
      <>
        <GlassModalHeader toggle={this.toggle}>
          Review Templates for "{companyName}"
        </GlassModalHeader>
        <GlassModalBody style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <p className="text-muted mb-3 small">
            Select which templates to create. You can always add more later.
          </p>

          {/* Entity Templates */}
          {entityTemplates.length > 0 && (
            <div className="mb-3">
              <h6 className="font-weight-bold">Client &amp; Entity Templates</h6>
              {entityTemplates.map((et, i) => (
                <div
                  key={i}
                  className={`d-flex align-items-start p-3 mb-2 rounded border ${enabledEntityTemplates[i] ? 'border-primary bg-light' : 'border-secondary'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.toggleEntityTemplate(i)}
                >
                  <input
                    type="checkbox"
                    checked={enabledEntityTemplates[i]}
                    onChange={() => this.toggleEntityTemplate(i)}
                    className="mt-1 mr-3"
                    style={{ cursor: 'pointer' }}
                  />
                  <div>
                    <strong>{et.reviewName || et.name || `Template ${i + 1}`}</strong>
                    {et.secondary && <span className="badge badge-secondary ml-2">Secondary</span>}
                    <div className="text-muted small mt-1">
                      {et.reviewDescription || `${(et.descriptions || []).length} fields`}
                    </div>
                    <div className="text-muted small">
                      Fields: {(et.descriptions || []).map(d => d.description).join(', ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Invoice Template */}
          {invoiceTemplate && (
            <div className="mb-3">
              <h6 className="font-weight-bold">Invoice Template</h6>
              <div className="d-flex align-items-center p-3 mb-2 rounded border border-success bg-light">
                <span className="text-success mr-3">✓</span>
                <div>
                  <strong>{invoiceTemplate.templateName || 'Standard Invoice'}</strong>
                  <div className="text-muted small mt-1">Always included — used to generate invoices</div>
                </div>
              </div>
            </div>
          )}

          {/* Appointment Types */}
          {appointmentTypes.length > 0 && (
            <div className="mb-3">
              <h6 className="font-weight-bold">Appointment Types</h6>
              {appointmentTypes.map((apt, i) => (
                <div
                  key={i}
                  className={`d-flex align-items-start p-3 mb-2 rounded border ${enabledAppointmentTypes[i] ? 'border-primary bg-light' : 'border-secondary'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.toggleAppointmentType(i)}
                >
                  <input
                    type="checkbox"
                    checked={enabledAppointmentTypes[i]}
                    onChange={() => this.toggleAppointmentType(i)}
                    className="mt-1 mr-3"
                    style={{ cursor: 'pointer' }}
                  />
                  <div>
                    <strong>{apt.name}</strong>
                    <div className="text-muted small mt-1">
                      {apt.reviewDescription || apt.description || ''}
                    </div>
                    <div className="text-muted small">
                      Statuses: {(apt.statuses || []).map(s => s.name).join(', ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Refinement */}
          <div className="mt-3 pt-3 border-top">
            <h6 className="font-weight-bold">Need something else?</h6>
            <div className="d-flex align-items-center">
              <Field
                label="Describe what to add..."
                value={refinement}
                onChange={e => this.setState({ refinement: e.target.value })}
                className="flex-grow-1 mb-0"
                onKeyPress={e => e.key === 'Enter' && this.handleRefine()}
              />
              <Btn
                variant="default"
                size="sm"
                className="ml-2"
                onClick={this.handleRefine}
                disabled={!refinement.trim() || refining}
                style={{ marginTop: '-8px', whiteSpace: 'nowrap' }}
              >
                {refining ? '...' : 'Add'}
              </Btn>
            </div>
          </div>
        </GlassModalBody>
        <GlassModalFooter>
          <Btn variant="secondary" onClick={() => this.setState({ wizardStep: 2 })}>Back</Btn>
          <Btn
            variant="primary"
            onClick={() => this.handleCreate(false)}
            disabled={creating}
          >
            {creating ? 'Creating...' : `Create${hasEnabled ? ' with Templates' : ''}`}
          </Btn>
        </GlassModalFooter>
      </>
    );
  }

  render() {
    if (this.props.user && !this.props.user._id) {
      return <Redirect to='/' />
    }

    const { sortField, sortDir } = this.state;

    const sortIcon = (field) => (
      <span style={{ marginLeft: 4, color: sortField === field ? '#333' : '#bbb' }}>
        {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
      </span>
    );

    const colLabel = (label, field) => (
      <span
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        onClick={() => this.handleTableSort(field)}
      >
        {label}{sortIcon(field)}
      </span>
    );

    var Company = [];

    if (this.props.companies && this.props.companies.length > 0) {
      const idToDate = (id) => {
        try {
          const ts = parseInt(String(id).substring(0, 8), 16) * 1000;
          return new Date(ts);
        } catch { return null; }
      };

      Company = this.props.companies.map(p => {
        const created = idToDate(p._id);
        return {
          name: p.companyName || '',
          created: created ? created.toLocaleDateString('en-ZA') : '',
          createdTs: created ? created.getTime() : 0,
          owner: p.ownerName || '',
          calendar: p.calendarCount || 0,
          invoices: p.invoiceCount || 0,
          view: <div style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              onClick={() => this.props.dispatchSelectCompany(p._id)}
              style={{ cursor: 'pointer', color: '#9e9e9e', display: 'inline-flex', alignItems: 'center', padding: '4px', borderRadius: '50%', transition: 'color 0.2s, background 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#f9a825'; e.currentTarget.style.background = '#fffde7'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#9e9e9e'; e.currentTarget.style.background = 'transparent'; }}
              title="Switch to this company"
            >
              <i className="fas fa-exchange-alt" style={{ fontSize: 18 }} />
            </span>
            <span
              onClick={() => this.setState({ settingsCompany: p, deleteConfirm: '' })}
              style={{ cursor: 'pointer', color: '#9e9e9e', display: 'inline-flex', alignItems: 'center', padding: '4px', borderRadius: '50%', transition: 'color 0.2s, background 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#c62828'; e.currentTarget.style.background = '#fdecea'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#9e9e9e'; e.currentTarget.style.background = 'transparent'; }}
              title="Delete Company"
            >
              <i className="fas fa-trash" style={{ fontSize: 18 }} />
            </span>
          </div>,
        };
      });

      Company.sort((a, b) => {
        const av = sortField === 'created' ? a.createdTs : a[sortField];
        const bv = sortField === 'created' ? b.createdTs : b[sortField];
        if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });
    }

    const data = {
      columns: [
        { label: colLabel('Name', 'name'), field: 'name', sort: 'disabled' },
        { label: colLabel('Created', 'created'), field: 'created', sort: 'disabled' },
        { label: colLabel('Owner', 'owner'), field: 'owner', sort: 'disabled' },
        { label: colLabel('Calendar', 'calendar'), field: 'calendar', sort: 'disabled' },
        { label: colLabel('Invoices', 'invoices'), field: 'invoices', sort: 'disabled' },
        { label: '', field: 'view', sort: 'disabled' }
      ],
      rows: Company
    };

    const { wizardStep } = this.state;

    return (
      <div className="content-wrapper pl-5 pr-5">

        <section className="content">
          <div className="container-fluid">

            <div className="companies-table-wrapper">
              <GlassDataTable data={data} entries={10} searching={true} />
            </div>

            <GlassFab onClick={this.toggle} />

            <GlassModal isOpen={this.state.modal} toggle={this.toggle} size="md">
              {wizardStep === 1 && this.renderStep1()}
              {wizardStep === 2 && this.renderStep2()}
              {wizardStep === 3 && this.renderStep3()}
            </GlassModal>

            {/* Company Settings Modal */}
            <GlassModal isOpen={!!this.state.settingsCompany} toggle={() => this.setState({ settingsCompany: null, deleteConfirm: '' })} size="md">
              {this.state.settingsCompany && (() => {
                const c = this.state.settingsCompany;
                const idToDate = (id) => { try { return new Date(parseInt(String(id).substring(0, 8), 16) * 1000).toLocaleDateString('en-ZA'); } catch { return ''; } };
                const canDelete = this.state.deleteConfirm === c.companyName;
                return (
                  <>
                    <GlassModalHeader toggle={() => this.setState({ settingsCompany: null, deleteConfirm: '' })}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i className="fas fa-trash" style={{ fontSize: 20, color: '#c62828' }} />
                        {c.companyName}
                      </span>
                    </GlassModalHeader>
                    <GlassModalBody>
                      <p className="mb-1"><strong>Created:</strong> {idToDate(c._id)}</p>
                      <p className="mb-3"><strong>Owner:</strong> {c.ownerName || '—'}</p>

                      <div style={{ border: '1px solid #dc3545', borderRadius: 6, padding: '1rem', marginTop: '1.5rem' }}>
                        <p className="text-danger font-weight-bold mb-2">Danger Zone</p>
                        <p className="text-muted small mb-3">
                          This will permanently delete <strong>{c.companyName}</strong> and ALL its data — invoices, clients, appointments, templates, and more. This cannot be undone.
                        </p>
                        <Field
                          label={`Type "${c.companyName}" to confirm`}
                          value={this.state.deleteConfirm}
                          onChange={e => this.setState({ deleteConfirm: e.target.value })}
                        />
                        <Btn
                          variant="danger"
                          onClick={this.handleDeleteCompany}
                          disabled={!canDelete || this.state.deleting}
                          style={{ width: '100%', marginTop: 8 }}
                        >
                          {this.state.deleting ? 'Deleting...' : 'Delete Company'}
                        </Btn>
                      </div>
                    </GlassModalBody>
                    <GlassModalFooter>
                      <Btn variant="secondary" onClick={() => this.setState({ settingsCompany: null, deleteConfirm: '' })}>Close</Btn>
                    </GlassModalFooter>
                  </>
                );
              })()}
            </GlassModal>

          </div>
        </section>
      </div>
    );
  }
}

export default CompanyView;
